import { computePosition, flip, shift, offset, autoUpdate } from "@floating-ui/dom"

let tooltipElement = null
let describedElement = null
let showTimeout = null
let hideTimeout = null
let lastHideTime = 0
let stopAutoUpdate = null
let visibilityObserver = null

const SHOW_DELAY = 300
const HIDE_DELAY = 150
const SKIP_DELAY_WINDOW = 300
const TOOLTIP_ID = "tooltip"

function getVisibilityObserver() {
  if (!visibilityObserver) {
    visibilityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting && entry.target === describedElement) {
          hide()
        }
      }
    })
  }
  return visibilityObserver
}

function observeVisibility(element) {
  getVisibilityObserver().observe(element)
}

function unobserveVisibility(element) {
  if (visibilityObserver) {
    visibilityObserver.unobserve(element)
  }
}

function resetElement(element) {
  unobserveVisibility(element)
  removeAriaDescribedBy(element)
  restoreNativeTooltip(element)
}

function getOrCreateTooltip() {
  if (tooltipElement && tooltipElement.isConnected) {
    return tooltipElement
  }

  tooltipElement = document.createElement("div")
  tooltipElement.id = TOOLTIP_ID
  tooltipElement.className = "tooltip"
  tooltipElement.setAttribute("role", "tooltip")
  tooltipElement.popover = "hint"
  tooltipElement.addEventListener("toggle", handleTooltipToggle)
  document.body.appendChild(tooltipElement)

  return tooltipElement
}

function handleTooltipToggle(event) {
  if (event.newState === "closed") {
    if (stopAutoUpdate) {
      stopAutoUpdate()
      stopAutoUpdate = null
    }

    if (describedElement) {
      resetElement(describedElement)
      describedElement = null
    }

    lastHideTime = Date.now()
  }
}

function addAriaDescribedBy(element) {
  const current = element.getAttribute("aria-describedby")
  if (current) {
    const ids = current.split(/\s+/)
    if (!ids.includes(TOOLTIP_ID)) {
      element.setAttribute("aria-describedby", `${current} ${TOOLTIP_ID}`)
    }
  } else {
    element.setAttribute("aria-describedby", TOOLTIP_ID)
  }
}

function removeAriaDescribedBy(element) {
  const current = element.getAttribute("aria-describedby")
  if (!current) return

  const ids = current.split(/\s+/).filter(id => id !== TOOLTIP_ID)
  if (ids.length > 0) {
    element.setAttribute("aria-describedby", ids.join(" "))
  } else {
    element.removeAttribute("aria-describedby")
  }
}

function getTooltipText(element) {
  return element.dataset.tooltip || element.getAttribute("title")
}

function handleMorphElement(event) {
  const element = event.target

  suppressNativeTooltip(element)

  // Update tooltip if this is the current element
  if (describedElement === element && tooltipElement) {
    addAriaDescribedBy(element)

    const text = getTooltipText(element)
    if (text) {
      tooltipElement.textContent = text
    }
  }
}

function suppressNativeTooltip(element) {
  const title = element.getAttribute("title")
  if (title) {
    element.dataset.tooltip = title
    element.removeAttribute("title")
  }

  if (!element.tooltipMorphHandler) {
    element.tooltipMorphHandler = handleMorphElement
    element.addEventListener("morphdom:updated", element.tooltipMorphHandler)
  }
}

function restoreNativeTooltip(element) {
  if (element.tooltipMorphHandler) {
    element.removeEventListener("turbo:morph-element", element.tooltipMorphHandler)
    delete element.tooltipMorphHandler
  }

  const title = element.dataset.tooltip
  if (title) {
    element.setAttribute("title", title)
    delete element.dataset.tooltip
  }
}

function updatePosition(element, tooltip, placement) {
  return computePosition(element, tooltip, {
    placement,
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 })
    ]
  }).then(({ x, y, placement: actualPlacement }) => {
    tooltip.style.left = `${x}px`
    tooltip.style.top = `${y}px`

    tooltip.classList.remove("top", "bottom")
    tooltip.classList.add(actualPlacement)

    // Calculate arrow position to point at center of element
    const elementRect = element.getBoundingClientRect()
    const elementCenter = elementRect.left + elementRect.width / 2
    tooltip.style.setProperty("--tooltip-arrow-x", `${elementCenter - x}px`)
  })
}

function isTextTruncated(element) {
  return element.scrollWidth > element.clientWidth ||
    element.scrollHeight > element.clientHeight
}

function show(element) {
  const text = getTooltipText(element)
  if (!text) return

  if (element.textContent.trim().includes(text) && !isTextTruncated(element)) return

  clearTimeout(hideTimeout)

  // Stop auto update on previous tooltip when switching between tooltips with
  // skip delay - Aaron, Sat Jan 24 2026
  if (stopAutoUpdate) {
    stopAutoUpdate()
    stopAutoUpdate = null
  }

  if (describedElement && describedElement !== element) {
    resetElement(describedElement)
  }

  const tooltip = getOrCreateTooltip()
  tooltip.textContent = text

  describedElement = element
  observeVisibility(element)
  addAriaDescribedBy(element)

  const placement = element.dataset.tooltipPosition || "top"

  // The position computation depends on the popover's rendered size, which is
  // zero while it is still closed (display: none). Showing it hidden gives it
  // real dimensions to measure, so the entry animation reveals it at its final
  // location rather than sliding from a position computed against a zero-size
  // box. When already showing (switching between adjacent tooltips), it simply
  // repositions. - Aaron, Fri Jun 12 2026
  const alreadyShowing = tooltip.matches(":popover-open")

  if (!alreadyShowing) {
    tooltip.style.visibility = "hidden"
    tooltip.showPopover()
  }

  updatePosition(element, tooltip, placement).then(() => {
    if (!tooltip.isConnected) return

    tooltip.style.visibility = ""

    stopAutoUpdate = autoUpdate(element, tooltip, () => {
      updatePosition(element, tooltip, placement)
    })
  })
}

function hide() {
  if (tooltipElement && tooltipElement.isConnected) {
    tooltipElement.hidePopover()
  }
}

function isHoverPointer(event) {
  return event.pointerType === "mouse" || event.pointerType === "pen"
}

function handlePointerEnter(event) {
  if (!isHoverPointer(event)) return
  if (!event.target.closest) return

  // Keep tooltip visible when hovering over it
  if (tooltipElement && tooltipElement.contains(event.target)) {
    clearTimeout(hideTimeout)
    return
  }

  const element = event.target.closest("[title], [data-tooltip]")
  if (!element) return

  suppressNativeTooltip(element)

  const text = getTooltipText(element)
  if (!text) return

  clearTimeout(hideTimeout)
  clearTimeout(showTimeout)

  const timeSinceLastHide = Date.now() - lastHideTime
  const skipDelay = describedElement || timeSinceLastHide < SKIP_DELAY_WINDOW
  const delay = skipDelay ? 0 : SHOW_DELAY

  showTimeout = setTimeout(() => show(element), delay)
}

function handlePointerLeave(event) {
  if (!isHoverPointer(event)) return
  if (!event.target.closest) return

  // Start hide timeout when leaving tooltip
  if (tooltipElement && tooltipElement.contains(event.target)) {
    hideTimeout = setTimeout(hide, HIDE_DELAY)
    return
  }

  const element = event.target.closest("[title], [data-tooltip]")
  if (!element) return

  clearTimeout(showTimeout)
  hideTimeout = setTimeout(hide, HIDE_DELAY)
}

function handleFocusIn(event) {
  if (!event.target.closest) return

  const element = event.target.closest("[title], [data-tooltip]")
  if (!element) return

  suppressNativeTooltip(element)

  const text = getTooltipText(element)
  if (!text) return

  clearTimeout(hideTimeout)
  clearTimeout(showTimeout)

  const timeSinceLastHide = Date.now() - lastHideTime
  const skipDelay = describedElement || timeSinceLastHide < SKIP_DELAY_WINDOW
  const delay = skipDelay ? 0 : SHOW_DELAY

  showTimeout = setTimeout(() => show(element), delay)
}

function handleFocusOut(event) {
  if (!event.target.closest) return

  const element = event.target.closest("[title], [data-tooltip]")
  if (!element) return

  clearTimeout(showTimeout)
  hideTimeout = setTimeout(hide, HIDE_DELAY)
}

export function activate() {
  document.addEventListener("pointerenter", handlePointerEnter, true)
  document.addEventListener("pointerleave", handlePointerLeave, true)
  document.addEventListener("focusin", handleFocusIn, true)
  document.addEventListener("focusout", handleFocusOut, true)
}
